import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, VIEW, EDIT } from "const"
import {
    StateType,
    hasProjectAccess,
    replaceHistoryState,
} from "client/utils"
import {
    PRM_CC_REQUIREMENT,
    StoreType,
    CcRequirementProps,

    LoadCcRequirementDictionaries,
    LoadAllCcRequirements,
    LoadCcRequirementParents,
    LoadCcRequirementChildren,

    setCcRequirementDictionariesLoaded,
    setCcRequirementList,
    setAssignedCcRequirementParents,
    setAssignedCcRequirementChildren,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    Loader,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import NotFound from "client/parts/notFound"
import Form from "./form"
import State from "./state"
import Parents from "./assigned/parents"
import Children from "./assigned/children"
import VersionList from "../versionList"

import {
    RequirementLayout,
    RequirementBodyColumn,
    RequirementStateColumn,
} from "./styled"

const DETAILS = "#details"
const VERSIONS = "#versions"

const menu = [DETAILS, VERSIONS]

const View = ({
    requirementId = null,
    hash = null,
}: CcRequirementProps) => {
    const dictionariesLoaded = useSelector((state: StoreType) => state.ccRequirement.dictionariesLoaded)
    const baseRoute = useSelector((state: StoreType) => state.ccRequirement.baseRoute)
    const requirementList = useSelector((state: StoreType) => state.ccRequirement.requirementList)
    const requirement = requirementList[requirementId] || null

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState(menu.includes(hash) ? hash : DETAILS)
    const [dictionaryLoadState, setDictionaryLoadState] = useState<StateType>(IDLE)
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (dictionariesLoaded) return
        setDictionaryLoadState(PENDING)

        LoadCcRequirementDictionaries(userData.accountId, project.id, dispatch).then(() => {
            setDictionaryLoadState(IDLE)
            dispatch(setCcRequirementDictionariesLoaded(true))
        })
    }, [dictionariesLoaded])

    useEffect(() => {
        setLoadState(PENDING)
        Promise.all([
            LoadAllCcRequirements(project.id).then((res) => {
                dispatch(setCcRequirementList(res))
            }),
            LoadCcRequirementParents(requirementId).then((res) => {
                dispatch(setAssignedCcRequirementParents(requirementId, res))
            }),
            LoadCcRequirementChildren(requirementId).then((res) => {
                dispatch(setAssignedCcRequirementChildren(requirementId, res))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [])

    const handleMenuClick = (tab: string) => {
        setActiveTab(tab)
        replaceHistoryState(`${baseRoute}/${VIEW}/${requirementId}${tab}`)
    }

    const viewAccess = hasProjectAccess(PRM_CC_REQUIREMENT, VIEW)
    const editAccess = hasProjectAccess(PRM_CC_REQUIREMENT, EDIT)

    const allowedToView = viewAccess || requirement.creator === userData.userId
    const allowedToEdit = editAccess || requirement.creator === userData.userId

    if (!allowedToView) return <AccessDenied />
    if (!requirement) return <NotFound />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{`${lang.viewRequirement} # ${requirement.id || ""}`}</PageTitle>
            </PageHeader>
            <PageContent>
                <PageMenu>
                    <PageMenuButton
                        isActive={activeTab === DETAILS}
                        onClick={() => handleMenuClick(DETAILS)}
                    >
                        {lang.details}
                    </PageMenuButton>
                    <PageMenuButton
                        isActive={activeTab === VERSIONS}
                        onClick={() => handleMenuClick(VERSIONS)}
                    >
                        {lang.versions}
                    </PageMenuButton>
                </PageMenu>
                <Tab show={activeTab === DETAILS}>
                    {
                        (
                            dictionaryLoadState === PENDING
                            || loadState === PENDING
                        ) && <Loader />
                    }

                    <RequirementLayout>
                        <RequirementBodyColumn>
                            <Form
                                requirement={requirement}
                                editAccess={allowedToEdit}
                            />
                            <Parents
                                requirement={requirement}
                                editAccess={allowedToEdit}
                            />
                            <Children
                                requirement={requirement}
                                editAccess={allowedToEdit}
                            />
                        </RequirementBodyColumn>

                        <RequirementStateColumn>
                            <State
                                requirement={requirement}
                                editAccess={allowedToEdit}
                            />
                        </RequirementStateColumn>
                    </RequirementLayout>
                </Tab>
                <Tab show={activeTab === VERSIONS}>
                    <VersionList requirement={requirement} />
                </Tab>
            </PageContent>
        </Container>
    )
}
export default View
